import type { DateTime } from "luxon";

import type { Slot } from "./buildTimeSlotList";

/**
 * How far out a patient may book. Patients are capped to this many weeks from
 * the moment they schedule so clinician calendars stay controllable.
 * Staff-facing scheduling is deliberately not capped.
 */
export const BOOKING_WINDOW_WEEKS = 6;

/**
 * The last bookable instant: end of the day `weeks` out, in `now`'s zone. The
 * whole final day is bookable, so a patient scheduling on the 18th can pick any
 * time on the 30th of the following month.
 */
export const getBookingWindowEnd = (
  now: DateTime,
  weeks: number = BOOKING_WINDOW_WEEKS
): DateTime => now.plus({ weeks }).endOf("day");

/**
 * A time is within the booking window when it is at or before the window end.
 * Comparison is on epoch millis, so it is timezone-independent. An invalid
 * `time` yields `NaN` and is treated as outside the window.
 */
export const isWithinBookingWindow = (
  time: DateTime,
  now: DateTime,
  weeks: number = BOOKING_WINDOW_WEEKS
): boolean => time.toMillis() <= getBookingWindowEnd(now, weeks).toMillis();

/** Drop slots starting after the booking window closes. Order is preserved. */
export const filterSlotsOutsideBookingWindow = <T extends Slot>(
  slots: T[],
  now: DateTime,
  weeks: number = BOOKING_WINDOW_WEEKS
): T[] =>
  slots.filter((slot) => isWithinBookingWindow(slot.start, now, weeks));
